export type ApiEnvironment =
  | 'dev'
  | 'staging'
  | 'staging-testpay'
  | 'production'
  | 'local'
  | 'other';

export const API_URLS: Record<Exclude<ApiEnvironment, 'other'>, string> = {
  dev: 'https://api.gh.var-meta.com',
  staging: 'https://stg.gh.var-meta.com',
  "staging-testpay": 'https://api-gh-paysegment.flawless.moe',
  production: 'https://api.gh.mochispark.com',
  local: 'http://localhost:8080',
};

// Service addresses follow the environment selected at login.
export const TRACKING_API_URLS: Record<Exclude<ApiEnvironment, 'other'>, string> = {
  dev: `${API_URLS.dev}/tracking`,
  staging: `${API_URLS.staging}/tracking`,
  'staging-testpay': `${API_URLS['staging-testpay']}/tracking`,
  production: 'https://api.gh.mochispark.com/tracking',
  local: 'http://localhost:8081',
};

export const MONITOR_API_URLS: Partial<Record<ApiEnvironment, string>> = {
  production: 'https://grpc.monitor.mochispark.com/monitor',
  staging: `${API_URLS.staging}/monitor`,
  local: 'http://localhost:6001',
};

const ENVIRONMENT_STORAGE_KEY = 'golden-hounds-api-environment';
const CUSTOM_URL_STORAGE_KEY = 'golden-hounds-api-custom-url';
const configuredUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '');

function configuredEnvironment(): ApiEnvironment {
  const match = Object.entries(API_URLS).find(([, url]) => url === configuredUrl);
  return (match?.[0] as ApiEnvironment | undefined) ||
    (configuredUrl ? 'other' : 'staging');
}

export function readApiEnvironment(): ApiEnvironment {
  const active = readActiveEnvironment();
  if (active) return active.environment;
  const stored = window.localStorage.getItem(ENVIRONMENT_STORAGE_KEY);
  return stored && ['dev', 'staging', 'production', 'local', 'other', 'staging-testpay'].includes(stored)
    ? (stored as ApiEnvironment)
    : configuredEnvironment();
}

function readActiveEnvironment(): { environment: ApiEnvironment; apiUrl: string } | null {
  try {
    const id = sessionStorage.getItem('golden-hounds-active-session');
    const record = id && JSON.parse(localStorage.getItem('golden-hounds-session:' + id) || 'null');
    return record && record.id === id && (record.environment === 'other' || Object.prototype.hasOwnProperty.call(API_URLS, record.environment)) && typeof record.apiUrl === 'string' ? record : null;
  } catch { return null; }
}

export function readCustomApiUrl(): string {
  return window.localStorage.getItem(CUSTOM_URL_STORAGE_KEY) ||
    (configuredEnvironment() === 'other' ? configuredUrl || '' : '');
}

export function saveApiEnvironment(
  environment: ApiEnvironment,
  customUrl = readCustomApiUrl()
): void {
  window.localStorage.setItem(ENVIRONMENT_STORAGE_KEY, environment);
  if (customUrl) {
    window.localStorage.setItem(CUSTOM_URL_STORAGE_KEY, customUrl);
  }
}

export function getApiBaseUrl(): string {
  const active = readActiveEnvironment();
  if (active) return active.apiUrl;
  const environment = readApiEnvironment();
  const url = environment === 'other' ? readCustomApiUrl() : API_URLS[environment];
  return url.replace(/\/$/, '');
}

export function isMonitorAvailable(): boolean {
  const environment = readApiEnvironment();
  return environment === 'staging' || environment === 'production' || environment === 'local';
}

export function getMonitorApiBaseUrl(): string {
  return MONITOR_API_URLS[readApiEnvironment()] || '';
}

export function getTrackingApiBaseUrl(): string {
  const environment = readApiEnvironment();
  return environment === 'other'
    ? `${getApiBaseUrl()}/tracking`
    : TRACKING_API_URLS[environment];
}

export function isPotAvailable(): boolean {
  return readApiEnvironment() !== 'production';
}
